import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { userProfile, userProfileedit, changePassword } from '../../../store/user/userThunk';
import { reseteditProfile } from '../../../store/user/userSlice';
import { toast } from 'react-toastify';
import userprofile from '../../../assets/images/user/userprofile.png';

const Profile = () => {
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const dispatch = useDispatch();
  const { profile, editprofile } = useSelector((state) => state.userservice);

  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState('profile'); // 'profile' or 'password'
  const [showPassword, setShowPassword] = useState({
    current: false,
    new: false,
    verify: false,
  });
  const inputFile = useRef(null);

  // Fetch profile on mount
  useEffect(() => {
    dispatch(userProfile());
  }, [dispatch]);

  // Profile form validation schema
const profileValidationSchema = Yup.object({
  fullName: Yup.string()
    .min(2, 'Full Name must be at least 2 characters')
    .max(50, 'Full Name must not exceed 50 characters')
    .matches(/^[a-zA-Z\s'-]+$/, 'Full Name can only contain letters, spaces, hyphens, and apostrophes')
    .required('Full Name is required'),
  userName: Yup.string()
    .min(3, 'User Name must be at least 3 characters')
    .max(30, 'User Name must not exceed 30 characters')
    .matches(/^[a-zA-Z0-9_]+$/, 'User Name can only contain letters, numbers, and underscores')
    .required('User Name is required'),
  email: Yup.string()
    .email('Invalid email format')
    .max(255, 'Email must not exceed 255 characters')
    .required('Email is required'),
  gender: Yup.string()
    .oneOf(['Male', 'Female', 'Other'], 'Please select a valid gender')
    .required('Gender is required'),
  dob: Yup.date()
    .max(new Date(), 'Date of Birth cannot be in the future')
    .test('age', 'You must be at least 13 years old', function (value) {
      const today = new Date();
      const birthDate = new Date(value);
      const age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        return age - 1 >= 13;
      }
      return age >= 13;
    })
    .required('Date of Birth is required'),
  phoneNumber: Yup.string()
    .matches(/^\+?[1-9]\d{1,14}$/, 'Phone Number must be a valid international format (e.g., +1234567890)')
    .required('Phone Number is required'),
  country: Yup.string()
    .min(2, 'Country must be at least 2 characters')
    .max(100, 'Country must not exceed 100 characters')
    .required('Country is required'),
  state: Yup.string()
    .min(2, 'State must be at least 2 characters')
    .max(100, 'State must not exceed 100 characters')
    .required('State is required'),
  city: Yup.string()
    .min(2, 'City must be at least 2 characters')
    .max(100, 'City must not exceed 100 characters')
    .required('City is required'),
  profileImage: Yup.mixed()
    .nullable()
    .test('fileType', 'Only image files are allowed', (value) => {
      if (!value) return true; // Allow null/undefined (no file uploaded)
      return value && ['image/jpeg', 'image/png', 'image/gif', 'image/webp'].includes(value.type);
    })
    .test('fileSize', 'File size must be less than 5MB', (value) => {
      if (!value) return true; // Allow null/undefined (no file uploaded)
      return value && value.size <= 5 * 1024 * 1024; // 5MB limit
    }),
});

  

  // Password change validation schema
  const passwordValidationSchema = Yup.object().shape({
    currentPassword: Yup.string().required('Current password is required'),
    newPassword: Yup.string()
      .required('Please enter Password')
      .min(8, 'Password must be at least 8 characters')
      .max(32, 'Password must be at most 32 characters')
      .matches(/[a-z]/, 'Password must include at least one lowercase letter')
      .matches(/[A-Z]/, 'Password must include at least one uppercase letter')
      .matches(/\d/, 'Password must include at least one number')
      .matches(/[!@#$%^&*(),.?":{}|<>]/, 'Password must include at least one special character'),
    verifyPassword: Yup.string()
      .required('Please confirm password')
      .oneOf([Yup.ref('newPassword'), null], 'Passwords must match'),
  });

  // Profile form submission
  const handleProfileSubmit = async (values) => {
    dispatch(userProfileedit(values))
      .then((res) => {
        toast.success('Profile updated successfully!');
        dispatch(userProfile());
        setIsEditing(false);
      })
      .catch((err) => {
        toast.error(err?.message || 'Failed to update profile');
      });
  };

  // Password change submission
  const handlePasswordSubmit = (values, { resetForm }) => {
    dispatch(
      changePassword({
        oldPassword: values.currentPassword,
        newPassword: values.newPassword,
      })
    )
      .unwrap()
      .then((result) => {
        if (result?.status === 200) {
          toast.success(result.message);
          resetForm();
        }
      })
      .catch((e) => {
        toast.error(e);
      });
  };

  // Social platform configurations
  const socialPlatforms = [
    {
      name: 'Instagram',
      key: 'iglogin',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
        </svg>
      ),
    },
    {
      name: 'Facebook',
      key: 'Fblogin',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385h-3.047v-3.47h3.047v-2.642c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953h-1.514c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385c5.737-.9 10.125-5.864 10.125-11.854z" />
        </svg>
      ),
    },
    {
      name: 'WhatsApp',
      key: 'wpLogin',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
        </svg>
      ),
    },
  ];

  // Calculate profile completion percentage
  const calculateProfileCompletion = (profile) => {
    if (!profile) return 0;
    const fields = [
      'fullName', 'userName', 'email', 'image', 'gender',
      'dob', 'phoneNumber', 'country', 'state', 'city',
    ];
    const completedFields = fields.filter(
      (field) => profile[field] !== null && profile[field] !== undefined && profile[field] !== ''
    );
    return Math.round((completedFields.length / fields.length) * 100);
  };

  // Toggle password visibility
  const togglePasswordVisibility = (field) => {
    setShowPassword((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  // Trigger file input click
  const onButtonClick = () => {
    inputFile.current.click();
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="bg-white rounded-3xl shadow-sm overflow-hidden">
        {/* Header */}
        <div className="bg-[#50b5ff]/10 p-6 flex justify-between items-center">
          <h1 className="text-2xl font-bold text-gray-800">Profile Details</h1>
          <div className="flex gap-2">
            <button
              onClick={() => setActiveTab('profile')}
              className={`px-4 py-2 rounded-full font-medium transition-all duration-300 ${activeTab === 'profile'
                ? 'bg-[#50b5ff] text-white'
                : 'bg-white text-[#50b5ff] border border-[#50b5ff]'
                }`}
            >
              Profile
            </button>
            <button
              onClick={() => setActiveTab('password')}
              className={`px-4 py-2 rounded-full font-medium transition-all duration-300 ${activeTab === 'password'
                ? 'bg-[#50b5ff] text-white'
                : 'bg-white text-[#50b5ff] border border-[#50b5ff]'
                }`}
            >
              Security
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="p-6">
          {activeTab === 'profile' && (
            <div className="flex flex-col lg:flex-row gap-8">
              {/* Left Column - Profile Image & Social */}
              <div className="w-full lg:w-1/3 flex flex-col items-center">
                {/* Profile Image */}
                <div className="relative mb-6 group">
                  <div className="absolute inset-0 rounded-full bg-[#50b5ff]/20 transform scale-110 group-hover:scale-125 transition-all duration-300"></div>
                  <div className="h-48 w-48 rounded-full border-4 border-[#50b5ff] overflow-hidden relative">
                    <img
                      src={
                        profile?.image
                          ? profile.image.includes('uploads')
                            ? `${BASE_URL}/${profile.image}`
                            : profile.image
                          : userprofile
                      }
                      alt="User Profile"
                      className="h-full w-full object-cover"
                    />
                  </div>
                </div>

                {/* User Name */}
                <h2 className="text-2xl font-bold text-center text-gray-800">
                  {profile?.fullName || profile?.userName || 'Name Not Available'}
                </h2>
                <h3 className="text-lg font-medium text-[#50b5ff] text-center mb-6">
                  {profile?.userName || 'Username Not Available'}
                </h3>

                {/* Social Media Connections */}
                <div className="w-full bg-gray-50 rounded-xl p-4 mb-6">
                  <h3 className="text-lg font-semibold text-gray-800 pb-2 mb-4 text-center">
                    <span className="border-b-2 border-[#50b5ff] pb-2">Connected Accounts</span>
                  </h3>
                  <div className="grid grid-cols-1 gap-3">
                    {socialPlatforms.map((platform) => (
                      <div
                        key={platform.key}
                        className={`flex items-center gap-2 p-2 rounded-lg transition-all duration-300 ${profile?.[platform.key]
                          ? 'bg-[#50b5ff]/10 text-[#50b5ff] border border-[#50b5ff]/30'
                          : 'bg-gray-100 text-gray-400 border border-gray-200'
                          }`}
                        title={profile?.[platform.key] ? `Connected with ${platform.name}` : `Not connected with ${platform.name}`}
                      >
                        <div className={`${profile?.[platform.key] ? 'text-[#50b5ff]' : 'text-gray-400'}`}>
                          {platform.icon}
                        </div>
                        <span className="font-medium text-sm">{platform.name}</span>
                        {profile?.[platform.key] && (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-4 w-4 text-green-500 ml-auto"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                              clipRule="evenodd"
                            />
                          </svg>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Two Factor Authentication Status */}
                <div className="w-full bg-gray-50 rounded-xl p-4">
                  <div
                    className={`flex items-center gap-3 p-3 rounded-lg ${profile?.is2FAEnabled
                      ? 'bg-green-50 text-green-700 border border-green-200'
                      : 'bg-yellow-50 text-yellow-700 border border-yellow-200'
                      }`}
                  >
                    <div className={`p-2 rounded-full ${profile?.is2FAEnabled ? 'bg-green-100' : 'bg-yellow-100'}`}>
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" viewBox="0 0 20 20" fill="currentColor">
                        <path
                          fillRule="evenodd"
                          d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <div>
                      <p className="font-medium">Two-Factor Authentication</p>
                      <p className="text-sm">{profile?.is2FAEnabled ? 'Enabled' : 'Not Enabled'}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column - Profile Form */}
              <div className="w-full lg:w-2/3">
                {/* Status Card */}
                <div className="bg-[#50b5ff]/5 rounded-xl p-4 mb-6 border border-[#50b5ff]/20">
                  <div className="flex flex-col md:flex-row items-center gap-4">
                    <div className="p-3 rounded-full bg-[#50b5ff]/20">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-[#50b5ff]" viewBox="0 0 20 20" fill="currentColor">
                        <path
                          fillRule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <div className="text-center md:text-left flex-1">
                      <h3 className="text-lg font-bold text-gray-800">Profile Completion Status</h3>
                      <p className="text-gray-600">Complete your profile information to unlock all features</p>
                    </div>
                    <div className="flex flex-col items-center gap-2">
                      <div className="w-32 bg-gray-200 rounded-full h-2.5">
                        <div
                          className="bg-[#50b5ff] h-2.5 rounded-full"
                          style={{ width: `${calculateProfileCompletion(profile)}%` }}
                        ></div>
                      </div>
                      <p className="text-sm text-gray-600">{calculateProfileCompletion(profile)}% Complete</p>
                    </div>
                    <button
                      onClick={() => setIsEditing(!isEditing)}
                      className="bg-[#50b5ff] hover:bg-[#3da5ef] text-white font-medium py-2 px-6 rounded-full transition-all duration-300 flex items-center gap-2"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                      </svg>
                      {isEditing ? 'Cancel Edit' : 'Edit Profile'}
                    </button>
                  </div>
                </div>

                {/* Profile Form */}
                <Formik
                  initialValues={{
                    fullName: profile?.fullName || '',
                    userName: profile?.userName || '',
                    email: profile?.email || '',
                    profileImage: null,
                    gender: profile?.gender || '',
                    dob: profile?.dob || '',
                    phoneNumber: profile?.phoneNumber || '',
                    country: profile?.country || '',
                    state: profile?.state || '',
                    city: profile?.city || '',
                    id: profile?._id || null,
                  }}
                  validationSchema={profileValidationSchema}
                  onSubmit={handleProfileSubmit}
                  enableReinitialize
                >
                  {({ values, setFieldValue, errors, touched }) => (
                    <Form>
                      {/* Personal Information */}
                      <div className="bg-white rounded-xl border border-gray-100 p-6 mb-6">
                        <h3 className="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-2 mb-4">
                          <span className="border-b-2 border-[#50b5ff] pb-2">Personal Information</span>
                        </h3>

                        {/* Profile Image */}
                        {/* {isEditing && (
                          <div className="mb-6 flex justify-center">
                            <div className="relative">
                              <div className="h-32 w-32 rounded-full border-2 border-[#50b5ff] overflow-hidden">
                                <img
                                  src={
                                    values?.profileImage
                                      ? URL.createObjectURL(values.profileImage)
                                      : profile?.image
                                        ? profile.image.includes('uploads')
                                          ? `${BASE_URL}/${profile.image}`
                                          : profile.image
                                        : userprofile
                                  }
                                  alt="profile-pic"
                                  className="h-full w-full object-cover"
                                />
                              </div>
                              <button
                                type="button"
                                onClick={onButtonClick}
                                className="absolute bottom-0 right-0 bg-[#50b5ff] text-white p-2 rounded-full hover:bg-[#3da5ef] transition-colors"
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  className="h-4 w-4"
                                  viewBox="0 0 20 20"
                                  fill="currentColor"
                                >
                                  <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                                </svg>
                              </button>
                              <input
                                ref={inputFile}
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={(event) => {
                                  const file = event.target.files?.[0];
                                  if (file) {
                                    // Revoke previous URL if it exists to prevent memory leaks
                                    if (values?.profileImage) {
                                      URL.revokeObjectURL(URL.createObjectURL(values.profileImage));
                                    }
                                    setFieldValue('profileImage', file);
                                  }
                                }}
                              />
                            </div>
                          </div>
                        )} */}


{isEditing && (
            <div className="mb-6 flex justify-center">
              <div className="relative">
                <div className="h-32 w-32 rounded-full border-2 border-[#50b5ff] overflow-hidden">
                  <img
                    src={
                      values?.profileImage
                        ? URL.createObjectURL(values.profileImage)
                        : profile?.image
                          ? profile.image.includes('uploads')
                            ? `${BASE_URL}/${profile.image}`
                            : profile.image
                          : userprofile
                    }
                    alt="profile-pic"
                    className="h-full w-full object-cover"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => inputFile.current?.click()}
                  className="absolute bottom-0 right-0 bg-[#50b5ff] text-white p-2 rounded-full hover:bg-[#3da5ef] transition-colors"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                  </svg>
                </button>
                <input
                  ref={inputFile}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    if (file) {
                      if (values?.profileImage) {
                        URL.revokeObjectURL(URL.createObjectURL(values.profileImage));
                      }
                      setFieldValue('profileImage', file);
                    }
                  }}
                />
              </div>
            </div>
          )}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          {/* Fullname */}
                          <div>
                            <div className="flex items-center gap-3 mb-3">
                              <div className="p-2 rounded-full bg-[#50b5ff]/10">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-[#50b5ff]" viewBox="0 0 20 20" fill="currentColor">
                                  <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                                </svg>
                              </div>
                              <div className="flex-1">
                                <p className="text-sm text-gray-500">Full Name</p>
                                {isEditing ? (
                                  <Field
                                    name="fullName"
                                    type="text"
                                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#50b5ff] mt-1 ${touched.fullName && errors.fullName ? 'border-red-500' : 'border-gray-300'
                                      }`}
                                    placeholder="Enter your full name"
                                  />
                                ) : (
                                  <p className="text-gray-800 font-medium py-2">{profile?.fullName || 'Not provided'}</p>
                                )}
                                <ErrorMessage name="fullName" component="div" className="text-red-500 text-sm mt-1" />
                              </div>
                            </div>
                          </div>



                          {/* Username */}
                          <div>
                            <div className="flex items-center gap-3 mb-3">
                              <div className="p-2 rounded-full bg-[#50b5ff]/10">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-[#50b5ff]" viewBox="0 0 20 20" fill="currentColor">
                                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-6-3a2 2 0 11-4 0 2 2 0 014 0zm-2 4a5 5 0 00-4.546 2.916A5.986 5.986 0 0010 16a5.986 5.986 0 004.546-2.084A5 5 0 0010 11z" clipRule="evenodd" />
                                </svg>
                              </div>
                              <div className="flex-1">
                                <p className="text-sm text-gray-500">Username</p>
                                {isEditing ? (
                                  <Field
                                    name="userName"
                                    type="text"
                                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#50b5ff] mt-1 ${touched.userName && errors.userName ? 'border-red-500' : 'border-gray-300'
                                      }`}
                                    placeholder="Enter your username"
                                  />
                                ) : (
                                  <p className="text-gray-800 font-medium py-2">{profile?.userName || 'Not provided'}</p>
                                )}
                                <ErrorMessage name="userName" component="div" className="text-red-500 text-sm mt-1" />
                              </div>
                            </div>
                          </div>


                          {/* Email */}
                          <div>
                            <div className='flex items-center gap-3 mb-3'>
                              <div className="p-2 rounded-full bg-[#50b5ff]/10">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-[#50b5ff]" viewBox="0 0 20 20" fill="currentColor">
                                  <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                                  <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                                </svg>
                              </div>
                              <div className="flex-1">
                                <p className="text-sm text-gray-500">Email</p>
                                {isEditing ? (
                                  <Field
                                    name="email"
                                    type="email"
                                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#50b5ff] mt-2 ${touched.email && errors.email ? 'border-red-500' : 'border-gray-300'
                                      }`}
                                    placeholder="Enter your email"
                                  />
                                ) : (
                                  <p className="text-gray-800 font-medium py-2">{profile?.email || 'Not provided'}</p>
                                )}
                                <ErrorMessage name="email" component="div" className="text-red-500 text-sm mt-1" />
                              </div>
                            </div>
                          </div>
                          {/* Phone Number */}
                          <div>
                            <div className="flex items-center gap-3 mb-3">
                              <div className="p-2 rounded-full bg-[#50b5ff]/10">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-[#50b5ff]" viewBox="0 0 20 20" fill="currentColor">
                                  <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                                </svg>
                              </div>
                              <div className="flex-1">
                                <p className="text-sm text-gray-500">Phone Number</p>
                                {isEditing ? (
                                  <Field
                                    name="phoneNumber"
                                    type="text"
                                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#50b5ff] mt-1 ${touched.phoneNumber && errors.phoneNumber ? 'border-red-500' : 'border-gray-300'
                                      }`}
                                    placeholder="Enter your phone number"
                                  />
                                ) : (
                                  <p className="text-gray-800 font-medium py-2">{profile?.phoneNumber || 'Not provided'}</p>
                                )}
                                <ErrorMessage name="phoneNumber" component="div" className="text-red-500 text-sm mt-1" />
                              </div>
                            </div>
                          </div>

                          {/* Gender */}
                          <div>
                            <div className="flex items-center gap-3 mb-3">
                              <div className="p-2 rounded-full bg-[#50b5ff]/10">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-[#50b5ff]" viewBox="0 0 20 20" fill="currentColor">
                                  <path fillRule="evenodd" d="M10 2a4 4 0 00-4 4v1H5a1 1 0 00-.994.89l-1 9A1 1 0 004 18h12a1 1 0 00.994-1.11l-1-9A1 1 0 0015 7h-1V6a4 4 0 00-4-4zM8 6a2 2 0 114 0v1H8V6z" clipRule="evenodd" />
                                </svg>
                              </div>
                              <div className="flex-1">
                                <p className="text-sm text-gray-500">Gender</p>
                                {isEditing ? (
                                  <div className="flex gap-4 mt-2">
                                    <label className="flex items-center">
                                      <Field type="radio" name="gender" value="Male" className="mr-2 text-[#50b5ff] focus:ring-[#50b5ff]" />
                                      Male
                                    </label>
                                    <label className="flex items-center">
                                      <Field type="radio" name="gender" value="Female" className="mr-2 text-[#50b5ff] focus:ring-[#50b5ff]" />
                                      Female
                                    </label>
                                    <label className="flex items-center">
                                      <Field type="radio" name="gender" value="Other" className="mr-2 text-[#50b5ff] focus:ring-[#50b5ff]" />
                                      Other
                                    </label>
                                  </div>
                                ) : (
                                  <p className="text-gray-800 font-medium py-2">{profile?.gender || 'Not provided'}</p>
                                )}
                                <ErrorMessage name="gender" component="div" className="text-red-500 text-sm mt-1" />
                              </div>
                            </div>
                          </div>



                          {/* Date of Birth */}
                          <div>
                            <div className="flex items-center gap-3 mb-3">
                              <div className="p-2 rounded-full bg-[#50b5ff]/10">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-[#50b5ff]" viewBox="0 0 20 20" fill="currentColor">
                                  <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                                </svg>
                              </div>
                              <div className="flex-1">
                                <p className="text-sm text-gray-500">Date of Birth</p>
                                {isEditing ? (
                                  <Field
                                    name="dob"
                                    type="date"
                                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#50b5ff] mt-1 ${touched.dob && errors.dob ? 'border-red-500' : 'border-gray-300'
                                      }`}
                                  />
                                ) : (
                                  <p className="text-gray-800 font-medium py-2">{profile?.dob || 'Not provided'}</p>
                                )}
                                <ErrorMessage name="dob" component="div" className="text-red-500 text-sm mt-1" />
                              </div>
                            </div>
                          </div>

                        </div>
                      </div>

                      {/* Contact Information */}

                      <div className="bg-white rounded-xl border border-gray-100 p-6 mb-6">
                        <h3 className="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-2 mb-4">
                          <span className="border-b-2 border-[#50b5ff] pb-2">Location Information</span>
                        </h3>
                        <div className="mt-6">
                          {/* City, State, Country Group */}
                          <div className="flex items-center gap-3 mb-4">
                            <div className="p-2 rounded-full bg-[#50b5ff]/10">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-6 w-6 text-[#50b5ff]"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            </div>
                            <div className="flex-1">
                              {/* <p className="text-sm text-gray-500">Location</p> */}
                              {isEditing ? (
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-2">
                                  {/* City */}
                                  <div>
                                    <p className="text-xs text-gray-500">City</p>
                                    <Field
                                      name="city"
                                      type="text"
                                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#50b5ff] mt-1 ${touched.city && errors.city ? 'border-red-500' : 'border-gray-300'
                                        }`}
                                      placeholder="Enter your city"
                                    />
                                    <ErrorMessage name="city" component="div" className="text-red-500 text-sm mt-1" />
                                  </div>
                                  {/* State */}
                                  <div>
                                    <p className="text-xs text-gray-500">State</p>
                                    <Field
                                      name="state"
                                      type="text"
                                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#50b5ff] mt-1 ${touched.state && errors.state ? 'border-red-500' : 'border-gray-300'
                                        }`}
                                      placeholder="Enter your state"
                                    />
                                    <ErrorMessage name="state" component="div" className="text-red-500 text-sm mt-1" />
                                  </div>
                                  {/* Country */}
                                  <div>
                                    <p className="text-xs text-gray-500">Country</p>
                                    <Field
                                      name="country"
                                      type="text"
                                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#50b5ff] mt-1 ${touched.country && errors.country ? 'border-red-500' : 'border-gray-300'
                                        }`}
                                      placeholder="Enter your country"
                                    />
                                    <ErrorMessage name="country" component="div" className="text-red-500 text-sm mt-1" />
                                  </div>
                                </div>
                              ) : (
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-2">
                                  <div className="bg-gray-50 p-2 rounded">
                                    <p className="text-xs text-gray-500">City</p>
                                    <p className="text-gray-800">{profile?.city || 'Not provided'}</p>
                                  </div>
                                  <div className="bg-gray-50 p-2 rounded">
                                    <p className="text-xs text-gray-500">State</p>
                                    <p className="text-gray-800">{profile?.state || 'Not provided'}</p>
                                  </div>
                                  <div className="bg-gray-50 p-2 rounded">
                                    <p className="text-xs text-gray-500">Country</p>
                                    <p className="text-gray-800">{profile?.country || 'Not provided'}</p>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>

                        </div>
                      </div>
                      {/* Submit/Cancel Buttons */}
                      {isEditing && (
                        <div className="flex justify-end gap-4">
                          <button
                            type="button"
                            onClick={() => setIsEditing(false)}
                            className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium py-2 px-6 rounded-full transition-all duration-300"
                          >
                            Cancel
                          </button>
                          <button
                            type="submit"
                            className="bg-[#50b5ff] hover:bg-[#3da5ef] text-white font-medium py-2 px-6 rounded-full transition-all duration-300"
                          >
                            Save Changes
                          </button>
                        </div>
                      )}
                    </Form>
                  )}
                </Formik>
              </div>
            </div>
          )}

          {/* Password Change Section */}
          {activeTab === 'password' && (
            <div className="w-full max-w-lg mx-auto">
              <div className="bg-white rounded-xl border border-gray-100 p-6">
                <h3 className="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-2 mb-4">
                  <span className="border-b-2 border-[#50b5ff] pb-2">Change Password</span>
                </h3>
                <Formik
                  initialValues={{
                    currentPassword: '',
                    newPassword: '',
                    verifyPassword: '',
                  }}
                  validationSchema={passwordValidationSchema}
                  onSubmit={handlePasswordSubmit}
                >
                  {({ handleReset }) => (
                    <Form>
                      <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Current Password</label>
                        <div className="relative">
                          <Field
                            name="currentPassword"
                            type={showPassword.current ? 'text' : 'password'}
                            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#50b5ff] mt-2`}
                            placeholder="Enter current password"
                          />
                          <button
                            type="button"
                            onClick={() => togglePasswordVisibility('current')}
                            className="absolute right-3 top-2 text-gray-500"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-5 w-5"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              {showPassword.current ? (
                                <path
                                  fillRule="evenodd"
                                  d="M3.707 2.293a1 1 0 00-1.414 1.414l14 14a1 1 0 001.414-1.414l-1.473-1.473A10.014 10.014 0 0019.542 10C17.965 5.396 13.93 2 9.5 2a9.97 9.97 0 00-4.5 1.068l-1.293-1.293zM9.5 4c3.59 0 6.824 2.686 7.946 6.552a8.02 8.02 0 01-2.47 3.94l-1.268-1.268A6.032 6.032 0 0015.5 10c0-3.314-2.686-6-6-6a5.98 5.98 0 00-2.582.586l-1.41-1.41A7.964 7.964 0 019.5 4zm-6 6a6 6 0 019.5 4.898l-1.47-1.47A3.99 3.99 0 019.5 14a4 4 0 01-4-4c0-.354.046-.698.132-1.032L3.5 6.836A7.964 7.964 0 002 10z"
                                  clipRule="evenodd"
                                />
                              ) : (
                                <path
                                  fillRule="evenodd"
                                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a3 3 0 10-2 5.292V11a1 1 0 102 0v-1a3 3 0 001-5.292z"
                                  clipRule="evenodd"
                                />
                              )}
                            </svg>
                          </button>
                        </div>
                        <ErrorMessage name="currentPassword" component="div" className="text-red-500 text-sm mt-1" />
                      </div>

                      <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-2">New Password</label>
                        <div className="relative">
                          <Field
                            name="newPassword"
                            type={showPassword.new ? 'text' : 'password'}
                            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#50b5ff] mt-2`}
                            placeholder="Enter new password"
                          />
                          <button
                            type="button"
                            onClick={() => togglePasswordVisibility('new')}
                            className="absolute right-3 top-2 text-gray-500"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-5 w-5"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              {showPassword.new ? (
                                <path
                                  fillRule="evenodd"
                                  d="M3.707 2.293a1 1 0 00-1.414 1.414l14 14a1 1 0 001.414-1.414l-1.473-1.473A10.014 10.014 0 0019.542 10C17.965 5.396 13.93 2 9.5 2a9.97 9.97 0 00-4.5 1.068l-1.293-1.293zM9.5 4c3.59 0 6.824 2.686 7.946 6.552a8.02 8.02 0 01-2.47 3.94l-1.268-1.268A6.032 6.032 0 0015.5 10c0-3.314-2.686-6-6-6a5.98 5.98 0 00-2.582.586l-1.41-1.41A7.964 7.964 0 019.5 4zm-6 6a6 6 0 019.5 4.898l-1.47-1.47A3.99 3.99 0 019.5 14a4 4 0 01-4-4c0-.354.046-.698.132-1.032L3.5 6.836A7.964 7.964 0 002 10z"
                                  clipRule="evenodd"
                                />
                              ) : (
                                <path
                                  fillRule="evenodd"
                                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a3 3 0 10-2 5.292V11a1 1 0 102 0v-1a3 3 0 001-5.292z"
                                  clipRule="evenodd"
                                />
                              )}
                            </svg>
                          </button>
                        </div>
                        <ErrorMessage name="newPassword" component="div" className="text-red-500 text-sm mt-1" />
                      </div>

                      <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Verify Password</label>
                        <div className="relative">
                          <Field
                            name="verifyPassword"
                            type={showPassword.verify ? 'text' : 'password'}
                            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#50b5ff] mt-2`}
                            placeholder="Confirm new password"
                          />
                          <button
                            type="button"
                            onClick={() => togglePasswordVisibility('verify')}
                            className="absolute right-3 top-2 text-gray-500"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-5 w-5"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              {showPassword.verify ? (
                                <path
                                  fillRule="evenodd"
                                  d="M3.707 2.293a1 1 0 00-1.414 1.414l14 14a1 1 0 001.414-1.414l-1.473-1.473A10.014 10.014 0 0019.542 10C17.965 5.396 13.93 2 9.5 2a9.97 9.97 0 00-4.5 1.068l-1.293-1.293zM9.5 4c3.59 0 6.824 2.686 7.946 6.552a8.02 8.02 0 01-2.47 3.94l-1.268-1.268A6.032 6.032 0 0015.5 10c0-3.314-2.686-6-6-6a5.98 5.98 0 00-2.582.586l-1.41-1.41A7.964 7.964 0 019.5 4zm-6 6a6 6 0 019.5 4.898l-1.47-1.47A3.99 3.99 0 019.5 14a4 4 0 01-4-4c0-.354.046-.698.132-1.032L3.5 6.836A7.964 7.964 0 002 10z"
                                  clipRule="evenodd"
                                />
                              ) : (
                                <path
                                  fillRule="evenodd"
                                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a3 3 0 10-2 5.292V11a1 1 0 102 0v-1a3 3 0 001-5.292z"
                                  clipRule="evenodd"
                                />
                              )}
                            </svg>
                          </button>
                        </div>
                        <ErrorMessage name="verifyPassword" component="div" className="text-red-500 text-sm mt-1" />
                      </div>

                      <div className="flex justify-end gap-4">
                        <button
                          type="button"
                          onClick={handleReset}
                          className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium py-2 px-6 rounded-full transition-all duration-300"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className="bg-[#50b5ff] hover:bg-[#3da5ef] text-white font-medium py-2 px-6 rounded-full transition-all duration-300"
                        >
                          Submit
                        </button>
                      </div>
                    </Form>
                  )}
                </Formik>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Profile;